import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pb } from '@/core/api';

type BonusWeek = { weekStart: string; position: number | null; bonus: number };
type Summary = { bonus: { total_bonus: number; weeks: BonusWeek[] } };

function formatWeekRange(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? 6 : day - 1;
  const mon = new Date(now);
  mon.setDate(now.getDate() - diff);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const fmt = (d: Date): string =>
    d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  return `${fmt(mon)} - ${fmt(sun)}`;
}

function nextPaymentDate(): string {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return next.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
}

function useBonusSummary(collectorId: string | undefined): Summary | undefined {
  const { data } = useQuery({
    queryKey: ['collector-summary', collectorId],
    queryFn: async (): Promise<Summary> =>
      pb.send(`/api/payments/collector/${collectorId}/summary`, {
        method: 'GET',
      }),
    enabled: !!collectorId,
    staleTime: 60_000,
  });
  return data;
}

function currentWeekBonus(data: Summary): BonusWeek | undefined {
  return data.bonus.weeks.at(-1) ?? undefined;
}

function BonusDetails({ week }: { week: BonusWeek | undefined }): ReactNode {
  const hasBonus = week && week.bonus > 0;
  return (
    <>
      <p className="mt-1 text-lg font-bold text-text-primary">
        {hasBonus ? `$${week.bonus}` : '—'}
      </p>
      {week?.position && (
        <p className="text-xs text-text-secondary">Puesto #{week.position}</p>
      )}
      <p className="mt-sm text-[10px] text-text-tertiary">
        {hasBonus
          ? 'Bonus semanal provisional — aún puede cambiar antes del cierre'
          : 'Sin bonus esta semana'}
      </p>
    </>
  );
}

export function BonusCard({ collectorId }: { collectorId: string }): ReactNode {
  const data = useBonusSummary(collectorId);
  if (!data) return null;
  return (
    <div className="rounded-xl bg-white/[0.02] p-base">
      <p className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary/60">
        Bonus semanal
      </p>
      <BonusDetails week={currentWeekBonus(data)} />
      <p className="text-[10px] text-text-tertiary">
        Periodo: {formatWeekRange()}
      </p>
      <p className="text-[10px] text-text-tertiary">
        Se incluirá en el pago del {nextPaymentDate()}
      </p>
    </div>
  );
}
